// @flow

import jscookie from 'js-cookie';
import {Permissions} from '../Permissions';
import {PermissionsAwareStorageManager} from '../PermissionsAwareStorageManager';
import {CookieManifest} from './CookieManifest';

import type {
  Category,
  EnforcementMode,
  RemoveCookieOptions,
  SetCookieOptions,
} from '../types';

/**
 * Configuration options which can be passed to the constructor of Cookies.
 */
export type CookiesConfig = {|
  /**
   * By default, the library will make an XHR call to determine whether the user is currently
   * located in an "enforcement zone" where cookie permissions are required before setting cookies.
   * Passing a value for enforcementMode will skip this call and use the specified value instead.
   */
  enforcementMode?: EnforcementMode,

  /**
   * A map of domain overrides which should be applied to entries in the cookie manifest. This
   * is used for testing; for example, the jest tests for manage/frontend run with the URL
   * set to https://manage-jest.stripe/jest/page. So, passing {'stripe.com': 'manage-jest.stripe'}
   * will cause all cookies which would have been set on stripe.com to be instead set on
   * manage-jest.stripe.
   */
  domainOverrides?: {[domain: string]: string},
|};

/**
 * Allows reading and writing of cookies while honoring a user's cookie permissions.
 * @param {CookiesConfig} config Optional configuration options.
 */
export class Cookies extends PermissionsAwareStorageManager {
  /** @private */
  _domainOverrides: {[domain: string]: string};

  constructor(config: CookiesConfig = {...null}) {
    super(config);
    this._domainOverrides = config.domainOverrides || {};
  }

  /**
   * Reads the value of the specified cookie.
   * @param {string} name The name of the cookie to read.
   * @returns The value of the cookie, or null if no value is set.
   */
  get(name: string): string | null {
    const cookie = CookieManifest.get(name);
    const value = jscookie.get(name);
    return value === undefined ? null : value;
  }

  /**
   * Attempts to write the specified value to the specified cookie. This will
   * only succeed if:
   *
   *   1. The cookie belongs to a necessary category, OR
   *   2. The user has set permissions which allow cookies of the category
   *      to which the cookie belongs, OR
   *   3. The enforcement mode is currently set to "open", indicating that
   *      the current user is not located within an enforcement zone.
   *
   * @param {string} name The name of the cookie to write.
   * @param {string} value The value to write for the cookie.
   * @param {SetCookieOptions} options Options for setting the cookie.
   * @returns A promise for a value which indicates whether the cookie was successfully set.
   */
  async set(
    name: string,
    value: string,
    options: SetCookieOptions = {},
  ): Promise<boolean> {
    const cookie = CookieManifest.get(name);

    const domain = cookie.resolveDomain(options.domain, this._domainOverrides);
    const expires = cookie.resolveExpiry(options.lifetime);

    if (cookie.httpOnly) {
      throw new Error(
        `Cannot set the cookie ${name} via JavaScript, since it is marked HttpOnly. ` +
          `Please visit go/cookies for more information!`,
      );
    }

    // If the cookie is in a category that isn't allowed for the user, block it.
    const isAllowed = await this.isCategoryAllowed(cookie.category);
    if (!isAllowed) return false;

    jscookie.set(name, value, {
      domain,
      expires,
      secure: cookie.secure,
      sameSite: cookie.sameSite,
    });

    return true;
  }

  /**
   * Deletes the value for the specified cookie.
   * @param {string} name The name of the cookie to delete.
   * @param {RemoveCookieOptions} options Options for removing the cookie.
   */
  remove(name: string, options: RemoveCookieOptions = {}): void {
    const cookie = CookieManifest.get(name);

    jscookie.remove(name, {
      domain: cookie.resolveDomain(options.domain, this._domainOverrides),
      secure: cookie.secure,
      sameSite: cookie.sameSite,
    });
  }
}
